import type { Locale } from "../../../shared/I18n";
import type { NativeContextRNProps } from "../../../shared/props/NativeContextRNProps";
import type { RestResponse } from "../../native-module/types/ViewJobNativeModule";
import type { RNVJNavigationTrigger } from "../types/RNVJNavigationTrigger";

import { ENABLE_REST_API_COMPARISON } from "../../viewjob-feature-flags.json";
import { makeVJJsonUrl } from "../lib/makeVJJsonUrl";
import useNativeQuery from "../lib/useNativeQuery";
import { logModelComparison } from "../util/modelComparison";
import { PROVIDER_NAME } from "../util/VJConstants";
import { createViewjobOneGraphModel, type ViewjobOneGraphModel } from "../viewjobOneGraphModel";

import { useEffect } from "react";

interface UseRestApiComparisonParams {
  url: string | null;
  locale: Locale;
  prforceGroups: string | null;
  getVJData: (url: string) => Promise<RestResponse<model.viewjob.ViewJobClientSideModel>>;
  nativeContext?: NativeContextRNProps;
  trigger?: RNVJNavigationTrigger;
  source?: string;
  graphqlModel: ViewjobOneGraphModel | null;
  graphqlData?: unknown; // Raw GraphQL response data
}

/**
 * Custom hook for comparing GraphQL and REST API responses
 * This hook is used for debugging purposes only and should be removed in production
 */
export const useRestApiComparison = ({
  url,
  locale,
  prforceGroups,
  getVJData,
  nativeContext,
  trigger,
  source,
  graphqlModel,
  graphqlData,
}: UseRestApiComparisonParams) => {
  // Add REST API call using useNativeQuery (only if feature flag is enabled)
  const [getViewJobData, restQueryState] = useNativeQuery<
    RestResponse<model.viewjob.ViewJobClientSideModel>
  >(
    PROVIDER_NAME,
    ENABLE_REST_API_COMPARISON && url != null ? makeVJJsonUrl(url, locale, prforceGroups) : null,
    getVJData,
    {
      nativeContext,
      trigger,
      source,
    },
  );

  // Trigger REST API call (only if feature flag is enabled)
  useEffect(() => {
    if (ENABLE_REST_API_COMPARISON) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME
      getViewJobData();
    }
  }, [getViewJobData]);

  // Create REST API model for comparison (only if feature flag is enabled)
  const restViewJobModel =
    ENABLE_REST_API_COMPARISON && restQueryState.status === "success" && restQueryState.data.body
      ? createViewjobOneGraphModel(restQueryState.data.body, locale)
      : null;

  // Compare models when both are available (only if feature flag is enabled)
  useEffect(() => {
    if (ENABLE_REST_API_COMPARISON && graphqlModel && restViewJobModel) {
      logModelComparison(graphqlModel, restViewJobModel, graphqlData);
    }
  }, [graphqlModel, restViewJobModel, graphqlData]);

  return {
    restQueryState,
    restViewJobModel,
  };
};
